import * as React from 'react';
import { Box, Grid, Stack, Typography } from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { EditPencilIcon } from '../../../components/icons';
import { readFieldValue } from '../../../components/FormComponents/dynamicForm';
import { CUSTOMER_FIELD, getCustomerRecordLabel, getGuarantorTypeLabel, GUARANTOR_FIELD, GUARANTOR_HOME_ADDRESS_PREFIX, GUARANTOR_TYPE, } from '../config/create_application_schema';
import { API_FIELD } from '../config/api_field_names';
import { getAllTitleOptions } from '../config/form_field_factories';
import ApplicationInfoBanner from './ApplicationInfoBanner';
import { WIZARD_COLORS } from '../../../components/FormComponents/dynamicForm';
var NOT_PROVIDED = '—';
var joinName = function () {
    var parts = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        parts[_i] = arguments[_i];
    }
    return parts
        .map(function (part) { return String(part !== null && part !== void 0 ? part : '').trim(); })
        .filter(Boolean)
        .join(' ');
};
var resolveTitleLabel = function (titleValue) {
    var match = getAllTitleOptions().find(function (option) { return String(option.value) === String(titleValue !== null && titleValue !== void 0 ? titleValue : ''); });
    return match ? match.label : '';
};
var SignerCard = function (_a) {
    var heading = _a.heading, badge = _a.badge, signerName = _a.signerName, signerTitle = _a.signerTitle, email = _a.email, phone = _a.phone;
    return (React.createElement(Box, { sx: {
            backgroundColor: '#EFF6FF',
            borderRadius: '8px',
            padding: '12px 14px',
            marginBottom: 1.5,
        } },
        React.createElement(Stack, { direction: "row", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.25 },
            React.createElement(Typography, { sx: { fontSize: 14, fontWeight: 700, color: WIZARD_COLORS.ink }, noWrap: true }, heading),
            React.createElement(Box, { component: "span", sx: {
                    flexShrink: 0,
                    padding: '4px 12px',
                    borderRadius: '999px',
                    fontSize: 12,
                    fontWeight: 600,
                    color: WIZARD_COLORS.surface,
                    backgroundColor: WIZARD_COLORS.brand,
                } }, badge)),
        React.createElement(Box, { sx: { backgroundColor: WIZARD_COLORS.surface, borderRadius: '8px', padding: '12px 14px' } },
            React.createElement(Typography, { sx: { fontSize: 12, color: WIZARD_COLORS.label, mb: 0.5 } }, "Document Signer"),
            React.createElement(Typography, { sx: { fontSize: 14, fontWeight: 700, color: WIZARD_COLORS.ink } }, signerName || NOT_PROVIDED),
            signerTitle && (React.createElement(Typography, { sx: { fontSize: 13, color: WIZARD_COLORS.body } }, signerTitle)),
            React.createElement(Stack, { direction: "row", alignItems: "center", gap: 0.75, mt: 1 },
                React.createElement(MailOutlineIcon, { sx: { fontSize: 15, color: WIZARD_COLORS.label } }),
                React.createElement(Typography, { sx: { fontSize: 13, color: '#2B7FFF', wordBreak: 'break-all' } }, email || NOT_PROVIDED)),
            React.createElement(Stack, { direction: "row", alignItems: "center", gap: 0.75, mt: 0.5 },
                React.createElement(CallOutlinedIcon, { sx: { fontSize: 15, color: WIZARD_COLORS.label } }),
                React.createElement(Typography, { sx: { fontSize: 13, color: '#2B7FFF' } }, phone || NOT_PROVIDED)))));
};
var ColumnHeader = function (_a) {
    var title = _a.title, onEdit = _a.onEdit;
    return (React.createElement(Stack, { direction: "row", alignItems: "center", justifyContent: "space-between", sx: {
            backgroundColor: WIZARD_COLORS.highlight,
            borderRadius: '8px',
            padding: '10px 14px',
            marginBottom: 1.5,
        } },
        React.createElement(Typography, { sx: { fontSize: 14, fontWeight: 700, color: WIZARD_COLORS.ink } }, title),
        React.createElement(Box, { component: "button", type: "button", onClick: onEdit, sx: {
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: 600,
                color: WIZARD_COLORS.ink,
                padding: 0,
                '& svg': { width: 13, height: 13 },
            } },
            React.createElement(EditPencilIcon, null),
            "Edit")));
};
var DocumentSignerPreviewStep = React.forwardRef(function (_a, ref) {
    var preview = _a.preview, onEditCustomers = _a.onEditCustomers, onEditGuarantors = _a.onEditGuarantors, collectsGuarantor = _a.collectsGuarantor;
    React.useImperativeHandle(ref, function () { return ({
        validate: function () { return true; },
        getPayload: function () { return ({}); },
        getFiles: function () { return ({}); },
        reset: function () { return undefined; },
    }); }, []);
    var customers = preview.customers, guarantors = preview.guarantors, signerGuarantors = preview.signerGuarantors, isGuarantorIncluded = preview.isGuarantorIncluded;
    var hasGuarantor = collectsGuarantor &&
        isGuarantorIncluded &&
        (guarantors.length > 0 || signerGuarantors.length > 0);
    return (React.createElement(Box, null,
        React.createElement(ApplicationInfoBanner, { isEmphasised: true, icon: React.createElement(TaskAltIcon, null), title: "Document Signer Preview:", message: hasGuarantor
                ? 'To make any changes, please go back to the respective sections (Customer Information or Guarantor Information).'
                : 'To make any changes, please go back to the Customer Information section.' }),
        React.createElement(Grid, { container: true, spacing: 2 },
            React.createElement(Grid, { item: true, xs: 12, md: hasGuarantor ? 6 : 12 },
                React.createElement(ColumnHeader, { title: "Customer Document Signer", onEdit: onEditCustomers }),
                customers.length === 0 && (React.createElement(Typography, { sx: { fontSize: 13, color: WIZARD_COLORS.label } }, "No customer captured yet.")),
                customers.map(function (customer, customerIndex) {
                    var _a, _b;
                    return (React.createElement(SignerCard, { key: "customer-signer-".concat(customerIndex), heading: String(readFieldValue(customer, CUSTOMER_FIELD.LegalRegisteredName, API_FIELD.LegalRegisteredName) ||
                            readFieldValue(customer, CUSTOMER_FIELD.DbaName, API_FIELD.DbaName) ||
                            '') || NOT_PROVIDED, badge: getCustomerRecordLabel(customerIndex), signerName: joinName(readFieldValue(customer, CUSTOMER_FIELD.SignerFirstName, API_FIELD.FirstName), readFieldValue(customer, CUSTOMER_FIELD.SignerLastName, API_FIELD.LastName)), signerTitle: resolveTitleLabel(readFieldValue(customer, CUSTOMER_FIELD.SignerTitle, API_FIELD.Title)), email: String((_a = readFieldValue(customer, CUSTOMER_FIELD.SignerEmail, API_FIELD.Email)) !== null && _a !== void 0 ? _a : ''), phone: String((_b = readFieldValue(customer, CUSTOMER_FIELD.SignerPhone, API_FIELD.PhoneNumber)) !== null && _b !== void 0 ? _b : '') }));
                })),
            hasGuarantor && (React.createElement(Grid, { item: true, xs: 12, md: 6 },
                React.createElement(ColumnHeader, { title: "Guarantor", onEdit: onEditGuarantors }),
                signerGuarantors.map(function (signerGuarantor, signerGuarantorIndex) { return (React.createElement(SignerCard, { key: "signer-guarantor-".concat(signerGuarantorIndex), heading: signerGuarantor.name || NOT_PROVIDED, badge: getGuarantorTypeLabel(GUARANTOR_TYPE.Individual), signerName: signerGuarantor.name, signerTitle: "", email: signerGuarantor.email, phone: signerGuarantor.phone })); }),
                guarantors.map(function (guarantor, guarantorIndex) {
                    var _a;
                    var isIndividualGuarantor = readFieldValue(guarantor, GUARANTOR_FIELD.GuarantorType, 'GurType') !== GUARANTOR_TYPE.Business;
                    var guarantorName = isIndividualGuarantor
                        ? joinName(readFieldValue(guarantor, GUARANTOR_FIELD.FirstName, API_FIELD.FirstName), readFieldValue(guarantor, GUARANTOR_FIELD.MiddleName, API_FIELD.MiddleName), readFieldValue(guarantor, GUARANTOR_FIELD.LastName, API_FIELD.LastName))
                        : String((_a = readFieldValue(guarantor, GUARANTOR_FIELD.BusinessName, API_FIELD.BusinessName)) !== null && _a !== void 0 ? _a : '');
                    var signerName = isIndividualGuarantor
                        ? guarantorName
                        : joinName(readFieldValue(guarantor, GUARANTOR_FIELD.SignerFirstName, API_FIELD.FirstName), readFieldValue(guarantor, GUARANTOR_FIELD.SignerMiddleName, API_FIELD.MiddleName), readFieldValue(guarantor, GUARANTOR_FIELD.SignerLastName, API_FIELD.LastName)) || guarantorName;
                    var readContact = function (signerField, addressSuffix, apiField) {
                        return String((isIndividualGuarantor
                            ? undefined
                            : readFieldValue(guarantor, signerField, apiField)) ||
                            readFieldValue(guarantor, "".concat(GUARANTOR_HOME_ADDRESS_PREFIX).concat(addressSuffix), apiField) ||
                            '');
                    };
                    return (React.createElement(SignerCard, { key: "guarantor-signer-".concat(guarantorIndex), heading: guarantorName || NOT_PROVIDED, badge: getGuarantorTypeLabel(isIndividualGuarantor
                            ? GUARANTOR_TYPE.Individual
                            : GUARANTOR_TYPE.Business), signerName: signerName, signerTitle: isIndividualGuarantor
                            ? ''
                            : resolveTitleLabel(readFieldValue(guarantor, GUARANTOR_FIELD.SignerTitle, API_FIELD.Title)), email: readContact(GUARANTOR_FIELD.SignerEmail, 'Email', API_FIELD.Email), phone: readContact(GUARANTOR_FIELD.SignerPhone, 'Phone', API_FIELD.PhoneNumber) }));
                }))))));
});
DocumentSignerPreviewStep.displayName = 'DocumentSignerPreviewStep';
export default DocumentSignerPreviewStep;
